import { pick } from '@/common/js/just.js'
import convertData, { reserveConvertData } from '@/data/toutiao/convert_data.js'

import { getConfigFormModel, getProjectFormModel, getPlanFormModel } from '../../model/form.js'
import { dataFormatter, externalDataFormatter } from '@/components/material-selector/utils.js'

import { getAccountById } from '@/api/ptom.data.model.js'
import { queryImageDetailByIds } from '@/api/common.model.js'
import { getCarouselsById, getBrandCategoryInfo } from '@/api/toutiao.local.model.js'
import { nativeAnchorGet } from '@/api/toutiao.model.js'
import { usePageStates } from '../useCommonComputeds.js'

import {
  AUDIENCE_EXTEND,
  DEFAULT_SCHEDULE_TIME,
  SCHEDULE_TIME_TYPE,
  BUDGET_OPTIMIZE_SWITCH,
  PRICING_TYPE,
  BID_TYPE,
  DEEP_BID_TYPE,
  DEEP_EXTERNAL_ACTION,
  IMAGE_MODE,
  AMEME_TYPE,
  SCHEDULE_TYPE,
  APP_PROMOTION_TYPE,
  CREATE_TYPE_ENUM,
  BID_VALUE_TYPE,
  MATERIAL_STYLE,
  PROJECT_AUDIENCE_TYPE,
  DELIVERY_TYPE,
  VIDEO_HP_VISIBILITY
} from '../../consts/index.js'

import { MATERIAL_TYPE_ID, MATERIAL_TYPE } from '@/components/material-selector/consts.js'
import { rebuildTargetingForm } from '../utils/index.js'
import { getToutiaoAdMarketingSceneByVersion } from '@/views/promote/targeting/model/toutiao_targeting_consts.js'

export const TOUTIAO_V3_MEDIA_VERSION = 'TOUTIAO3.0'

const LEGACY_VIDEO_HP_VISIBILITY_MAP = Object.freeze({
  ON: VIDEO_HP_VISIBILITY.ON,
  OFF: VIDEO_HP_VISIBILITY.OFF
})

export function isToutiaoV3Task(taskInfo = {}) {
  return taskInfo.inner_plan_params?.media_version === TOUTIAO_V3_MEDIA_VERSION
}

export function getImageMaterialId(material = {}) {
  return material.image_id || material.images?.[0]?.image_id || ''
}

export function getMaterialInnerId(id) {
  if (typeof id === 'string' && id.startsWith('@')) return Number(id.slice(1))
  return id
}

export async function getToutiaoModifyForm(taskInfo) {
  const { taskId, task_progress, task_status, convert_name, ad_account_name, ad_account_id, outer_plan_params, creative_params } = taskInfo
  const { operation: projectOperation } = outer_plan_params
  const { operation: creativeOperation } = creative_params
  const isV3 = isToutiaoV3Task(taskInfo)
  const adMarketingScene = getToutiaoAdMarketingSceneByVersion(isV3)
  const targetingParam = taskInfo.targetings?.targetingParam || {}
  const audienceType = isV3 && Object.keys(targetingParam).length === 0 ? PROJECT_AUDIENCE_TYPE.UNLIMITED : PROJECT_AUDIENCE_TYPE.CUSTOM
  const accountInfo = await getAccountById({ ad_account_id })
  const targetingId = taskInfo.targetings?.targetingId || taskInfo.targetings?.caohuaTargetingId
  const targetingForm = {
    targetingParamJson:
      audienceType === PROJECT_AUDIENCE_TYPE.UNLIMITED
        ? null
        : {
          ...rebuildTargetingForm(taskInfo),
          ad_marketing_scene: adMarketingScene,
          account: { id: ad_account_id, name: ad_account_name }
        },
    targetingId,
    // 兼容新建模版 src/views/create/bytedance/hooks/utils/plan.js makeInnerParams 构建audience_push时会用到
    accountIds: ad_account_id
  }

  const creative = await rebuildToutiaoCreativeForm(taskInfo)

  const projectForm = {
    ...getConfigFormModel({ isV3 }),
    isV3,
    taskId,
    task_progress,
    task_status,
    ad_account_id,
    // 保存额外辅助配置信息，用于创意组再次随机打乱分配
    recombineHelpers: {
      // 智能创意组分配表单，
      distributeForm: null
    },

    accounts: [{ ...accountInfo }],
    project: {
      ...getProjectFormModel({ isV3 }),
      ...rebuildToutiaoProjectForm(taskInfo, { isV3, audienceType }),
      operation: projectOperation === 'ENABLE' ? projectOperation : 'DISABLE'
    },
    plan: {
      ...getPlanFormModel(),
      ...(await rebuildToutiaoPlanForm(taskInfo)),
      operation: creativeOperation === 'ENABLE' ? creativeOperation : 'DISABLE'
    },
    targetings: [targetingForm],
    creatives: [creative],
    descriptions: [],
    landingPages: []
  }
  return projectForm
}

function rebuildToutiaoProjectForm(taskInfo, { isV3 = false, audienceType = PROJECT_AUDIENCE_TYPE.CUSTOM } = {}) {
  const projectDefaults = getProjectFormModel({ isV3 })
  const { ad_account_id, task_progress, campaign_params = {} } = taskInfo
  const { plan_name, ch_platform_id, game_id, star_task_id, package: ad_package, callback_switch } = taskInfo.inner_plan_params
  const {
    micro_app_instance_id,
    instance_id,
    ad_type,
    landing_type,
    micro_promotion_type,
    delivery_medium,
    app_promotion_type,
    subscribe_url = '',
    download_url,
    star_auto_material_addition_switch,
    delivery_mode,
    delivery_type,
    marketing_goal,

    search_bid_ratio,
    keywords = [],
    audience_extend,

    delivery_setting = {},
    external_action,
    deep_external_action = DEEP_EXTERNAL_ACTION.PURCHASE_ROI
  } = taskInfo.outer_plan_params
  let keywordsDesc = ''
  const keywordCounts = keywords.length
  if (keywordCounts) {
    const [firstItem] = keywords
    keywordsDesc = `已选${firstItem.keyword}...共${keywordCounts}个关键词`
  } else {
    keywordsDesc = '未添加关键词，待自动更新'
  }
  const keywordSwitchParams = {
    keywordSwitch: typeof search_bid_ratio !== 'undefined' || typeof audience_extend !== 'undefined' ? 1 : 0,
    search_bid_ratio: search_bid_ratio || '',
    keywords,
    keywordsDesc,
    audience_extend: audience_extend || AUDIENCE_EXTEND.OFF
  }

  const {
    deep_bid_type = DEEP_BID_TYPE.DEFAULT,
    bid_type,
    budget,
    budget_mode,
    schedule_time,
    schedule_type,
    bid_type_upgrade = BID_TYPE.CUSTOM,
    pricing = PRICING_TYPE.PRICING_OCPM,
    budget_optimize_switch = BUDGET_OPTIMIZE_SWITCH.OFF,
    cpa_bid = '',
    first_roi_goal = '',
    roi_goal,
    start_time,
    end_time,
    bid_range,
    bid_num_range,
    roi_bid_range,
    roi_bid_num_range
  } = delivery_setting

  const cpaBidParams = {
    cpa_bid,
    first_roi_goal,
    bid_value_type: BID_VALUE_TYPE.FIXED,
    roi_bid_value_type: BID_VALUE_TYPE.FIXED,
    bid_num_range: [],
    roi_bid_num_range: []
  }

  if (bid_range) {
    cpaBidParams.bid_num_range = bid_num_range
    cpaBidParams.bid_value_type = BID_VALUE_TYPE.RANGE
  }

  if (roi_bid_range) {
    cpaBidParams.roi_bid_num_range = roi_bid_num_range
    cpaBidParams.roi_bid_value_type = BID_VALUE_TYPE.RANGE
  }
  const optimizeGoalParams = {
    external_action,
    deep_bid_type,
    deep_external_action
  }

  const date_range = []
  if (schedule_type === SCHEDULE_TYPE.START_END) {
    date_range.push(start_time)
    date_range.push(end_time)
  }

  const convertId = getConvertId(taskInfo.outer_plan_params)
  const { isModify } = usePageStates()

  let createType = CREATE_TYPE_ENUM.NEW
  if (isModify.value) {
    // 头条2.0项目是否可以修改
    // 1、是项目广告（项目名称 = 广告名称）
    // 2、步骤标记 <= 3
    if (task_progress <= 3 && campaign_params.project_name === plan_name) {
      createType = CREATE_TYPE_ENUM.NEW
    } else {
      createType = CREATE_TYPE_ENUM.INHERIT
    }
  }
  return {
    // 基础信息
    createType,
    ad_account_id,
    plan_name: isModify.value ? plan_name : `${plan_name}_复制`,
    project_name: campaign_params.project_name,
    ch_platform_id: ch_platform_id || projectDefaults.ch_platform_id,
    callback_switch: callback_switch ?? projectDefaults.callback_switch,
    game: {
      id: game_id,
      ad_package
    },

    convertId,

    // 营销目的及场景
    landing_type: landing_type || projectDefaults.landing_type, // 推广目的
    app_promotion_type: app_promotion_type || projectDefaults.app_promotion_type, // 子目的（V3仅用于兼容表单，不提交）
    ad_type: ad_type || projectDefaults.ad_type,
    subscribe_url,
    download_url,
    micro_app_instance_id: isV3 ? instance_id ?? '' : micro_app_instance_id ?? '',
    micro_promotion_type: isV3
      ? delivery_medium || micro_promotion_type || projectDefaults.micro_promotion_type
      : micro_promotion_type || projectDefaults.micro_promotion_type,
    star_task: isV3 ? {} : { star_task_id },
    star_task_list: [],
    star_auto_material_addition_switch: star_auto_material_addition_switch || projectDefaults.star_auto_material_addition_switch,
    delivery_mode: delivery_mode || projectDefaults.delivery_mode,
    delivery_type: isV3 ? DELIVERY_TYPE.NORMAL : delivery_type || projectDefaults.delivery_type,
    marketing_goal: marketing_goal || projectDefaults.marketing_goal,
    audience_type: audienceType,

    // 搜索快投
    ...keywordSwitchParams,

    // 项目排期与出价
    delivery_setting: {
      ...projectDefaults.delivery_setting,
      date_range,
      bid_type: bid_type || projectDefaults.delivery_setting.bid_type,
      budget: budget ?? projectDefaults.delivery_setting.budget,
      budget_mode: budget_mode || projectDefaults.delivery_setting.budget_mode,
      schedule_time: schedule_time || projectDefaults.delivery_setting.schedule_time,
      schedule_type: schedule_type || projectDefaults.delivery_setting.schedule_type,
      schedule_time_type: schedule_time === DEFAULT_SCHEDULE_TIME ? SCHEDULE_TIME_TYPE.ALL : SCHEDULE_TIME_TYPE.RANGE,
      bid_type_upgrade,
      pricing,
      budget_optimize_switch,
      cpa_bid,
      roi_goal: roi_goal ?? projectDefaults.delivery_setting.roi_goal,
      ...cpaBidParams,
      optimize_goal: { ...optimizeGoalParams }
    }
  }
}

export async function rebuildToutiaoPlanForm(taskInfo) {
  const creativeParams = pick(taskInfo.creative_params, Object.keys(getPlanFormModel()))
  const {
    product_info,
    brand_info,
    web_url_material_list = [],
    native_setting,
    title_material_list = [],
    anchor_material_list = [],
    text_abstract_list = []
  } = taskInfo.creative_params
  const { call_to_action_buttons = [], materials_type = MATERIAL_STYLE.PROMOTION_MATERIALS, ...restParams } = creativeParams
  const { image_ids, selling_points, titles } = product_info || {}
  if (image_ids?.length) {
    const imageDetailIds = image_ids.map(getMaterialInnerId)
    const [res] = await queryImageDetailByIds({ imageDetailIds })
    // res 可能为 undefined（接口返回空数组），兜底避免 res.items 崩溃
    const [image] = res?.items || []
    if (image) {
      restParams.app_image = {
        ...image,
        url: image.source_url
      }
    }
  }
  const { aweme_id, anchor_related_type, video_hp_visibility, is_feed_and_fav_see: legacyVideoHpVisibilityRaw } = native_setting || {}
  const legacyVideoHpVisibility = LEGACY_VIDEO_HP_VISIBILITY_MAP[legacyVideoHpVisibilityRaw] || ''
  if (aweme_id) {
    restParams.aweme_id = aweme_id
    if (video_hp_visibility || legacyVideoHpVisibility) {
      restParams.video_hp_visibility = video_hp_visibility || legacyVideoHpVisibility
    }
  }
  if (anchor_related_type) {
    restParams.anchor_related_type = anchor_related_type
  }

  const anchorParams = {
    anchor_material_list,
    anchor_name: ''
  }

  const abstractTextParams = {
    text_abstract: '',
    text_abstract_list: []
  }

  if (text_abstract_list?.length) {
    abstractTextParams.text_abstract_list = text_abstract_list.map((text) => text.abstract_text)
  }

  const { cdp_brand_id, yuntu_category_id, brand_name_id } = brand_info || {}
  const brandCategoryParams = {
    cdp_brand_id: cdp_brand_id || '',
    brand_name_id: brand_name_id || '',
    yuntu_category_id: []
  }
  if (yuntu_category_id) {
    const { brand_category_info } = await getBrandCategoryInfo({ ad_account_id: taskInfo.ad_account_id })
    const paths = findPathById(brand_category_info, String(yuntu_category_id))
    brandCategoryParams.yuntu_category_id = paths
  }

  // 获取手动锚点名称
  if (anchor_material_list.length) {
    const { list: anchorList } = await nativeAnchorGet({
      adAccountId: taskInfo.ad_account_id,
      pageIndex: 1,
      pageSize: 100, // 设置为100个，如果超出需要开发接口根据id查询单个锚点信息的详情
      status: 'AUDIT_SUCCESS',
      source: ['CUSTOM', 'MANUAL'].join(',')
    })

    const [anchor] = anchor_material_list
    const anchorInfo = anchorList.find((i) => i.anchor_id === anchor.anchor_id)
    if (anchorInfo?.anchor_name) {
      anchorParams.anchor_name = anchorInfo.anchor_name
    }
  }

  return {
    ...restParams,
    ...anchorParams,
    materials_type,
    app_name: titles ? titles[0] : '',
    title_material_list,
    selling_points: '',
    selling_points_list: selling_points || [],
    call_to_action_buttons_list: call_to_action_buttons,
    web_url_material_list,

    ...abstractTextParams,
    ...brandCategoryParams
  }
}

export async function rebuildToutiaoCreativeForm(taskInfo) {
  const {
    campaign_name,
    video_material_list = [],
    image_material_list = [],
    carousel_material_list = [],
    awemeIdentity,
    materials = []
  } = taskInfo.creative_params

  let imageAudio = []
  let image = []
  let video = []
  if (awemeIdentity === AMEME_TYPE.AWEME_VIDEO) {
    for (let i = 0; i < video_material_list.length; i++) {
      const image = video_material_list[i]

      // 获取全部的达人视频id，在数据加载完后进行筛选，勾选已选达人视频
      // src/views/create/bytedance/modify.vue watch taskList.value
      // if (image.image_mode === IMAGE_MODE.VIDEO_VERTICAL && image.auth_type) {
      if (image.auth_type) {
        video.push({
          ...image,
          video_info: { item_id: image.item_id }
        })
      }
    }
  } else {
    const { carousel_push } = taskInfo.inner_plan_params
    const { from_account_id } = carousel_push ? carousel_push[0] : {}
    const imageIds = image_material_list.map(getImageMaterialId).filter(Boolean).map(getMaterialInnerId)
    const videoIds = video_material_list
      .map((i) => {
        return {
          videoId: getMaterialInnerId(i.video_id),
          coverId: getMaterialInnerId(i.video_cover_id)
        }
      })
      .filter((item) => item.videoId)

    if (carousel_material_list?.length) {
      const carousels = await getCarouselsById({
        ad_account_id: taskInfo.ad_account_id,
        carousels: carousel_material_list.map((c) => c.carousel_id).join(',')
      })

      imageAudio = carousels.map((item) => ({
        ...item,
        type: MATERIAL_TYPE.IMAGE_AUDIO,
        name: item.file_name,
        width: item.images[0].width,
        height: item.images[0].height,
        url: item.images[0].url,
        preview_url: item.images[0].url,
        imageAudioAccount: { id: from_account_id }
      }))
    }
    if (imageIds?.length) {
      const imageRes = await queryImageDetailByIds({ imageDetailIds: imageIds })
      image = imageRes.map((i) => dataFormatter.image(i.items[0]))
    }
    if (videoIds?.length) {
      const defaultCoverIds = []
      const imageCoverIds = []

      materials.forEach((i) => {
        if (typeof i.defaultCover !== 'undefined') {
          if (i.defaultCover === true) {
            defaultCoverIds.push(i.inner_id)
          } else {
            imageCoverIds.push(i.inner_id)
          }
        }
      })
      const videoRes = await queryImageDetailByIds({ imageDetailIds: videoIds.map((i) => i.videoId) })
      const imageCovers = imageCoverIds.length ? await queryImageDetailByIds({ imageDetailIds: imageCoverIds }) : []
      const defaultCovers = defaultCoverIds.length ? await queryImageDetailByIds({ frameIds: defaultCoverIds }) : []
      video = videoRes.map((i) => dataFormatter.video(i.items[0]))
      video = video.map((v) => {
        // 找到视频id和封面id 为一组的项，然后修改当前获取到的视频的封面
        const { coverId } = videoIds.find((_v) => _v.videoId === v.id) || {}
        const cover = [...imageCovers, ...defaultCovers].find((i) => i.items[0].id === coverId)

        if (cover) {
          return {
            ...v,
            cover_id: cover.items[0].id,
            cover_width: cover.items[0].width,
            cover_height: cover.items[0].height,
            cover_size: cover.items[0].size,
            cover_url: cover.items[0].cover_url
          }
        }

        return v
      })
    }
  }

  return {
    creative_name: campaign_name || '',
    materialType: MATERIAL_TYPE_ID[MATERIAL_TYPE.VIDEO],
    selectedMaterials: {
      image,
      imageAudio,
      video
    }
  }
}

/**
 * @description: 根据优化目标和深度优化方式获取convertId
 * @param {*} outer_plan_params
 * @return {*}
 */
function getConvertId(outer_plan_params) {
  let { external_action, deep_external_action, app_promotion_type } = outer_plan_params
  if (JSON.stringify(deep_external_action) === '{}' || !deep_external_action) deep_external_action = ''

  const convertList = app_promotion_type === APP_PROMOTION_TYPE.RESERVE ? reserveConvertData : convertData
  const convertItem = convertList.find((i) => i.external_action === external_action && i.deep_external_action === deep_external_action)
  return convertItem?.key
}

function findPathById(tree, targetId) {
  const path = []

  function dfs(nodes, currentPath) {
    for (const node of nodes) {
      const newPath = [...currentPath, node.id]
      if (node.id === targetId) {
        path.push(...newPath)
        return true
      }
      if (node.children && node.children.length > 0) {
        if (dfs(node.children, newPath)) {
          return true
        }
      }
    }
    return false
  }

  dfs(tree, [])
  return path
}
